import type { EmployerSnapshot } from '@klukka/client';
import {
  fmt,
  fmtDate,
  fmtDay,
  fmtH,
  fmtTime,
  hoursOpen,
  isOverlong,
  maxShiftHours,
  type AdminLang,
  type PeriodInfo,
} from '@klukka/core';
import { useState } from 'react';
import { dailyCost, hoursOf, presenceToday, sumDayCosts, totals, type EmployeeCalc } from '../derive';
import { AdminChip, KPI, Panel, type Tr } from '../widgets';

/** A time as a datetime-local input wants it, in the browser's own zone. */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The live picture: what the period has cost so far, who is on the clock, and
 * where each person stands on the timesheet that gates their payout.
 */
export function DashboardTab({ snap, all, pinfo, tr, lang, onClockOut }: {
  snap: EmployerSnapshot;
  all: EmployeeCalc[];
  pinfo: PeriodInfo;
  tr: Tr;
  lang: AdminLang;
  onClockOut: (empId: string, at: string) => void;
}) {
  const settings = snap.company.settings;
  const tot = totals(all);
  const presence = presenceToday(snap);
  const inCount = presence.filter((p) => p.key === 'in').length;
  const days = dailyCost(snap, all, pinfo);
  const daySum = sumDayCosts(days);
  const periodKey = `${pinfo.start}_${pinfo.end}`;
  // The shift being clocked out by hand, and the end time typed so far.
  const [closing, setClosing] = useState<{ empId: string; since: string; at: string } | null>(null);

  // A short shift most likely ended just now; a long one most likely ended
  // when the working day did, and the administrator corrects from there.
  const suggestedEnd = (since: string) =>
    hoursOpen({ clockInAt: since }) <= settings.dailyThreshold
      ? Date.now()
      : Date.parse(since) + settings.dailyThreshold * 3_600_000;

  const presenceText = (p: (typeof presence)[number]) => {
    if (p.key === 'in') {
      // A shift that did not start today says which day it did.
      const day = p.since!.slice(0, 10) === snap.serverToday ? '' : `${fmtDay(p.since!.slice(0, 10))} `;
      return `${tr('workingSince')} ${day}${fmtTime(p.since!)}`;
    }
    if (p.key === 'sick') return tr('sick');
    if (p.key === 'vacation') return tr('vacationLbl');
    if (p.key === 'out') return `${tr('clockedOut')} · ${fmtH(p.hours ?? 0)} klst`;
    return tr('notClockedIn');
  };

  return (
    <div>
      <div className="kpis">
        <KPI label={tr('grossAccrued')} value={fmt(tot.gross)} sub={tr('liabToDate')} />
        <KPI label={tr('netPayable')} value={fmt(tot.net)} sub={tr('liabToDate')} />
        <KPI
          label={tr('fullCost')}
          value={fmt(tot.cost)}
          sub={`≈ ${Math.round((tot.cost / (tot.gross || 1)) * 100)}% × ${tr('gross').toLowerCase()}`}
          accent
        />
        <KPI
          label={tr('clockedInNow')}
          value={`${inCount} / ${presence.length}`}
          sub={fmtDate(snap.serverToday)}
          accent={inCount > 0}
        />
      </div>

      <Panel title={tr('presenceToday')}>
        {presence.map((p) => {
          const editing = p.key === 'in' && closing?.empId === p.emp.id ? closing : null;
          const hours = editing ? (Date.parse(editing.at) - Date.parse(editing.since)) / 3_600_000 : 0;
          return (
            <div key={p.emp.id}>
              <div className="ap-row" style={{ padding: '8px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="trail-dot" style={{ background: p.color }} />
                  <b>{p.emp.name}</b>
                  <span className="dimtx">· {p.emp.role}</span>
                  {p.key === 'in' && isOverlong({ clockInAt: p.since! }, settings) ? (
                    <span className="chip" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}>
                      {tr('overMax').replace('{max}', fmtH(maxShiftHours(settings)))}
                    </span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="dimtx mono" style={{ fontSize: 13 }}>{presenceText(p)}</span>
                  {p.key === 'in' && !editing ? (
                    <button
                      className="btn-sm"
                      onClick={() => setClosing({ empId: p.emp.id, since: p.since!, at: toLocalInput(suggestedEnd(p.since!)) })}
                    >
                      {tr('clockOutBtn')}
                    </button>
                  ) : null}
                </div>
              </div>
              {editing ? (
                <div style={{ padding: '4px 0 12px 20px' }}>
                  <div className="s-row" style={{ alignItems: 'flex-end' }}>
                    <label className="s-fld" style={{ width: '220px' }}>
                      {tr('shiftEnded')}
                      <span className="s-inp-wrap">
                        <input
                          className="inp"
                          type="datetime-local"
                          value={editing.at}
                          onChange={(e) => setClosing({ ...editing, at: e.target.value })}
                        />
                      </span>
                    </label>
                    <span className="mono" style={{ paddingBottom: 8 }}>
                      {Number.isFinite(hours)
                        ? tr('hoursOnDay').replace('{h}', fmtH(hours)).replace('{d}', fmtDay(editing.since.slice(0, 10)))
                        : '—'}
                    </span>
                    <button
                      className="btn-sm ok"
                      disabled={!Number.isFinite(hours) || hours < 0}
                      onClick={() => {
                        onClockOut(p.emp.id, new Date(editing.at).toISOString());
                        setClosing(null);
                      }}
                    >
                      {tr('clockOutBtn')}
                    </button>
                    <button className="btn-sm" onClick={() => setClosing(null)}>{tr('cancel')}</button>
                  </div>
                  <div className="dimtx" style={{ fontSize: 12 }}>{tr('clockOutHelp')}</div>
                </div>
              ) : null}
            </div>
          );
        })}
      </Panel>

      <Panel title={tr('perDayCost')}>
        <table className="tbl">
          <thead>
            <tr>
              <th>{tr('dayCol')}</th>
              <th>{tr('gross')}</th>
              <th>{tr('orlofLbl')}</th>
              <th>{tr('pensionTot')} {settings.pensionErPct}%</th>
              <th>{tr('fundsLbl')}</th>
              <th>{tr('tryggLbl')} {settings.tryggPct}%</th>
              <th>{tr('fullCost')}</th>
            </tr>
          </thead>
          <tbody>
            {days.filter((d) => d.gross > 0 || d.isToday).map((d) => (
              <tr key={d.date} style={d.isToday ? { background: 'var(--tint)' } : undefined}>
                <td>
                  <b>{fmtDay(d.date)}</b>
                  {d.holiday ? (
                    <span className="chip" style={{ borderColor: 'var(--ot2)', color: 'var(--ot2)', marginLeft: 6 }}>
                      {lang === 'is' ? 'Frídagur' : 'Holiday'}
                    </span>
                  ) : d.weekend ? (
                    <span className="dimtx" style={{ marginLeft: 6 }}>{lang === 'is' ? 'helgi' : 'weekend'}</span>
                  ) : null}
                  {d.isToday ? (
                    <span className="chip" style={{ borderColor: 'var(--live)', color: 'var(--live)', marginLeft: 6 }}>
                      {lang === 'is' ? 'í dag' : 'today'}
                    </span>
                  ) : null}
                </td>
                <td className="mono">{fmt(d.gross)}</td>
                <td className="mono">{fmt(d.orlof)}</td>
                <td className="mono">{fmt(d.pension)}</td>
                <td className="mono">{fmt(d.funds)}</td>
                <td className="mono">{fmt(d.trygg)}</td>
                <td className="mono"><b>{fmt(d.cost)}</b></td>
              </tr>
            ))}
            <tr className="sum">
              <td>{tr('total')}</td>
              <td className="mono">{fmt(daySum.gross)}</td>
              <td className="mono">{fmt(daySum.orlof)}</td>
              <td className="mono">{fmt(daySum.pension)}</td>
              <td className="mono">{fmt(daySum.funds)}</td>
              <td className="mono">{fmt(daySum.trygg)}</td>
              <td className="mono"><b>{fmt(daySum.cost)}</b></td>
            </tr>
          </tbody>
        </table>
      </Panel>

      <Panel>
        <table className="tbl">
          <thead>
            <tr>
              <th>{tr('name')}</th><th>{tr('hoursCol')}</th><th>{tr('gross')}</th><th>{tr('net')}</th>
              <th>{tr('cost')}</th><th>{tr('orlofDays')}</th><th>{tr('tsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {all.map(({ emp, calc }) => {
              const sheet = snap.timesheets.find(
                (w) => w.empId === emp.id && w.periodKey === periodKey && w.status !== 'rejected',
              );
              return (
                <tr key={emp.id}>
                  <td>
                    <b>{emp.name}</b>
                    <span className="dimtx"> · {emp.role}</span>
                    {calc.rows[calc.rows.length - 1]?.sick ? (
                      <span className="chip" style={{ borderColor: 'var(--warn)', color: 'var(--warn)', marginLeft: 8 }}>
                        {tr('sickToday')}
                      </span>
                    ) : null}
                  </td>
                  <td className="mono">{fmtH(hoursOf(calc))}</td>
                  <td className="mono">
                    {fmt(calc.gross)}
                    {calc.adjGross ? (
                      <span className="dimtx" style={{ fontSize: 11 }}>
                        {' '}({tr('adjCol')} {calc.adjGross > 0 ? '+' : ''}{fmt(calc.adjGross)})
                      </span>
                    ) : null}
                  </td>
                  <td className="mono">{fmt(calc.net)}</td>
                  <td className="mono"><b>{fmt(calc.totalCost)}</b></td>
                  <td className="mono">{fmtH(calc.orlofDays)} d.</td>
                  <td>
                    {sheet ? (
                      <AdminChip status={sheet.status} lang={lang} />
                    ) : (
                      <span className="chip" title={tr('tsNote')} style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}>
                        {tr('tsMissing')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="dimtx" style={{ marginTop: 10, fontSize: 12 }}>{tr('tsNote')}</div>
      </Panel>
    </div>
  );
}
